/**
 * 환율 설정 FE 타입. 고도몰식 고정 환율 + 자동(관세청 배치)/수동 모드 정책.
 * 운영자가 통화별 KRW 환산 환율을 직접 관리하거나, 공공데이터포털(관세청) 주간환율을
 * 자동 갱신하도록 설정한다. (be-catalog ID19 계약 — 도착 시 path/shape 정합.)
 *
 * 환율 의미: krwPerUnit = 외화 1단위당 KRW. 예) USD krwPerUnit=1350 → 1 USD = 1,350 KRW.
 * 최종환율(krwPerUnit) = AUTO 모드일 때 baseRate + adjustment, MANUAL 모드일 때 수동 입력값.
 * 상품 외화 자동계산 = KRW 기준가 ÷ krwPerUnit.
 */

/** 환율을 적용하는 외화 통화 (KRW 는 기준 통화라 환율 대상이 아니다). */
export type ExchangeRateCurrency = 'USD' | 'JPY' | 'CNY';

export const EXCHANGE_RATE_CURRENCIES: ReadonlyArray<ExchangeRateCurrency> = ['USD', 'JPY', 'CNY'];

/** 환율 갱신 모드: AUTO=관세청 배치 자동 갱신(+조정), MANUAL=운영자 수동 입력. */
export type ExchangeRateMode = 'AUTO' | 'MANUAL';

export const EXCHANGE_RATE_MODES: ReadonlyArray<ExchangeRateMode> = ['AUTO', 'MANUAL'];

/** GET /admin/v1/exchange-rates 목록 항목 (ExchangeRateResponse). */
export interface ExchangeRate {
  currencyCode: ExchangeRateCurrency;
  /** 갱신 모드. 구계약(미존재) 호환을 위해 optional — 없으면 MANUAL 로 간주. */
  mode?: ExchangeRateMode;
  /** AUTO 모드의 기준 환율(관세청 원천값). MANUAL 모드면 null. */
  baseRate?: number | null;
  /** 기준 환율에 가감하는 조정값(±). 기본 0. */
  adjustment?: number;
  /** 최종 적용 환율 = 외화 1단위당 KRW. 양수. */
  krwPerUnit: number;
  /** be-catalog 가 내려주는 krwPerUnit alias(최종환율). 표시상 krwPerUnit 과 동일. */
  finalKrwPerUnit?: number;
  active: boolean;
  updatedAt: string | null;
  /** 마지막 수정자(관리자 식별). 표시용. */
  updatedBy?: string | null;
}

/**
 * PUT /admin/v1/exchange-rates/{currencyCode} body (ExchangeRateUpdateRequest).
 * MANUAL 모드면 krwPerUnit 이 최종값. AUTO 모드면 adjustment 만 보내고 최종값은 BE 가 base+adjustment 로 계산.
 */
export interface ExchangeRateUpdatePayload {
  /** MANUAL 모드 최종값. AUTO 모드는 BE 가 base+adjustment 로 산출하므로 생략 가능(미조회 시 omit). */
  krwPerUnit?: number;
  active: boolean;
  mode?: ExchangeRateMode;
  adjustment?: number;
}

/** 자동 갱신 공급자. 현재 관세청 공공데이터포털만 지원. */
export type ExchangeRateProvider = 'KCS_PUBLICDATA';

/** GET /admin/v1/exchange-rates/provider (ExchangeRateProviderResponse). api_key 는 write-only(마스킹). */
export interface ExchangeRateProviderConfig {
  provider: ExchangeRateProvider;
  enabled: boolean;
  /** 키가 저장되어 있는지 여부(dormant/active 판정용). be-catalog 필드명. */
  apiKeyConfigured: boolean;
  /** 저장된 API 키의 마스킹 표현(예: ••••••••). 미설정이면 null. 원문은 절대 내려오지 않는다. */
  apiKeyMasked: string | null;
  /** 매일 자동 갱신 시각 "HH:mm:ss" (LocalTime). */
  scheduleTime: string | null;
  lastRunAt: string | null;
  /** 마지막 배치 결과 상태(SUCCESS/FAILED/SKIPPED/null). */
  lastStatus: string | null;
  lastMessage: string | null;
}

/**
 * PUT /admin/v1/exchange-rates/provider body (ExchangeRateProviderUpdateRequest).
 * apiKey 는 write-only — null/미포함이면 기존 키 보존, ""=삭제, 값=교체.
 * scheduleTime 은 "HH:mm:ss" LocalTime.
 */
export interface ExchangeRateProviderConfigPayload {
  enabled: boolean;
  scheduleTime?: string | null;
  /** 새 API 키. 미변경 시 omit(undefined). 명시적으로 비우려면 빈 문자열. */
  apiKey?: string;
}

/** POST /admin/v1/exchange-rates/provider/test 응답 (연동 테스트 — persist 안 함). */
export interface ExchangeRateProviderTestResult {
  success: boolean;
  provider?: ExchangeRateProvider;
  message: string | null;
  /** 라이브 호출로 받은 샘플 환율(통화별 1단위당 KRW). 실패 시 비거나 null. */
  sampleRates: ReadonlyArray<{ currencyCode: string; krwPerUnit: number }> | null;
}

/** POST /admin/v1/exchange-rates/refresh 응답 (ExchangeRateRefreshResponse). */
export interface ExchangeRateRefreshResult {
  success: boolean;
  updatedCount: number;
  message: string | null;
  rates: ReadonlyArray<ExchangeRate>;
}

/** 환율 변경 출처. */
export type ExchangeRateChangeSource = 'MANUAL' | 'AUTO' | 'TEST';

/** GET /admin/v1/exchange-rates/history 항목 (ExchangeRateHistoryResponse). */
export interface ExchangeRateHistoryItem {
  id: string;
  currencyCode: ExchangeRateCurrency;
  oldKrwPerUnit: number | null;
  newKrwPerUnit: number;
  baseRate: number | null;
  adjustment: number | null;
  mode: ExchangeRateMode;
  source: ExchangeRateChangeSource;
  changedBy: string | null;
  changedAt: string;
}
